/**
 * Tasks API Service
 */

import api from './api';
import type { ApiResponse, RoutingTask, TaskSummary } from '../types';

interface TasksResponse {
  tasks: RoutingTask[];
}

/** Inclusive date range (YYYY-MM-DD), Baghdad calendar days. */
export interface TaskDateRange {
  from: string;
  to: string;
}

export function isTaskDateRange(
  value?: string | string[] | TaskDateRange
): value is TaskDateRange {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Get tasks with optional date(s) and status filters
 * @param dates - Optional date filter(s) (YYYY-MM-DD) - can be a single date string or array of dates
 * @param statusSlugs - Optional array of status slugs to filter by
 */
export async function getTasks(
  dates?: string | string[] | TaskDateRange,
  statusSlugs?: string[]
): Promise<{ tasks: RoutingTask[] }> {
  /*
    A range goes to the server as one query (`from`/`to`), not as N day-by-day
    requests. The multi-date path below fires one HTTP call per date sequentially,
    which is tolerable for two days and not for seven.
  */
  if (isTaskDateRange(dates)) {
    const queryParams = new URLSearchParams({ from: dates.from, to: dates.to });
    if (statusSlugs && statusSlugs.length > 0) {
      statusSlugs.forEach(status => queryParams.append('statusSlugs', status));
    }
    const response = await api.get<ApiResponse<TasksResponse>>(`/tasks?${queryParams.toString()}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch tasks');
    }
    return response.data.data;
  }

  // Normalize dates to array
  const dateArray = dates ? (Array.isArray(dates) ? dates : [dates]) : [];

  // If single date, use the original API call
  if (dateArray.length <= 1) {
    const queryParams = new URLSearchParams();
    if (dateArray.length === 1) queryParams.set('date', dateArray[0]);
    if (statusSlugs && statusSlugs.length > 0) {
      statusSlugs.forEach(status => queryParams.append('statusSlugs', status));
    }
    
    const url = queryParams.toString() ? `/tasks?${queryParams.toString()}` : '/tasks';
    const response = await api.get<ApiResponse<TasksResponse>>(url);
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch tasks');
    }
    
    return response.data.data;
  }
  
  // For multiple dates, fetch each date and combine results
  const allTasks: RoutingTask[] = [];
  const seenUids = new Set<string>();
  
  for (const date of dateArray) {
    const queryParams = new URLSearchParams();
    queryParams.set('date', date);
    if (statusSlugs && statusSlugs.length > 0) {
      statusSlugs.forEach(status => queryParams.append('statusSlugs', status));
    }
    
    const url = `/tasks?${queryParams.toString()}`;
    const response = await api.get<ApiResponse<TasksResponse>>(url);
    
    if (response.data.success && response.data.data) {
      // Add tasks, avoiding duplicates by UID
      for (const task of response.data.data.tasks) {
        if (!seenUids.has(task.uid)) {
          seenUids.add(task.uid);
          allTasks.push(task);
        }
      }
    }
  }
  
  return { tasks: allTasks };
}

/**
 * Get tasks by UIDs - fetches tasks regardless of their status
 * @param uids - Array of task UIDs to fetch
 */
export async function getTasksByUids(uids: string[]): Promise<{ tasks: RoutingTask[] }> {
  const response = await api.post<ApiResponse<{ tasks: RoutingTask[] }>>(
    '/tasks/by-uids',
    { uids }
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch tasks by UIDs');
  }
  
  return response.data.data;
}

/**
 * Mark a task as done via Core API trusted endpoint
 * @param taskUid - The task UID (string UID from Core DB)
 */
export async function markTaskAsDone(taskUid: string): Promise<void> {
  const response = await api.post<ApiResponse<void>>(
    `/tasks/${taskUid}/mark-as-done`
  );
  
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to mark task as done');
  }
}

/**
 * Get task counts grouped by date for a date range (for calendar badges)
 */
export async function getTaskCountsByDateRange(startDate: string, endDate: string): Promise<{ date: string; count: number }[]> {
  const queryParams = new URLSearchParams();
  queryParams.set('startDate', startDate);
  queryParams.set('endDate', endDate);

  const response = await api.get<ApiResponse<{ counts: { date: string; count: number }[] }>>(
    `/tasks/counts-by-date?${queryParams.toString()}`
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch task counts');
  }

  return response.data.data.counts;
}

/**
 * Get task summary
 */
export async function getTaskSummary(cityId: number, date?: string): Promise<TaskSummary> {
  const queryParams = new URLSearchParams();
  queryParams.set('cityId', cityId.toString());
  if (date) queryParams.set('date', date);

  const response = await api.get<ApiResponse<TaskSummary>>(
    `/tasks/summary?${queryParams.toString()}`
  );
  
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch task summary');
  }
  
  return response.data.data;
}
